import { useMemo } from 'react';

import { Card, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { formatDateTime } from '@/shared/lib/format/dateTime';
import { formatMetricValue } from '@/shared/lib/utils';

import { BYTES_TO_GB, BYTES_TO_MB } from '@/features/monitoring/api';
import type { PodOverviewData } from '@/features/monitoring/api';

import styles from './PodOverviewTable.module.css';

const { Title } = Typography;

type PodOverviewTableProps = {
  data: PodOverviewData[];
  isLoading: boolean;
};

export const PodOverviewTable = ({ data, isLoading }: PodOverviewTableProps) => {
  const columns: ColumnsType<PodOverviewData> = useMemo(
    () => [
      {
        title: 'Time',
        dataIndex: 'timestamp',
        key: 'timestamp',
        width: 180,
        fixed: 'left',
        render: (timestamp: number) => formatDateTime(timestamp * 1000),
      },
      {
        title: 'Pod',
        dataIndex: 'pod',
        key: 'pod',
        width: 200,
        fixed: 'left',
        ellipsis: true,
      },
      {
        title: 'CPU Usage',
        dataIndex: 'cpuUsage',
        key: 'cpuUsage',
        width: 120,
        align: 'right',
        render: (value: number | null) => (value != null ? formatMetricValue(value) : '-'),
      },
      {
        title: 'CPU Requests',
        dataIndex: 'cpuRequests',
        key: 'cpuRequests',
        width: 120,
        align: 'right',
        render: (value: number | null) => (value != null ? formatMetricValue(value) : '-'),
      },
      {
        title: 'CPU Requests %',
        dataIndex: 'cpuRequestsPercent',
        key: 'cpuRequestsPercent',
        width: 140,
        align: 'right',
        render: (value: number | null) => (value != null ? `${formatMetricValue(value)}%` : '-'),
      },
      {
        title: 'CPU Limits',
        dataIndex: 'cpuLimits',
        key: 'cpuLimits',
        width: 120,
        align: 'right',
        render: (value: number | null) => (value != null ? formatMetricValue(value) : '-'),
      },
      {
        title: 'CPU Limits %',
        dataIndex: 'cpuLimitsPercent',
        key: 'cpuLimitsPercent',
        width: 140,
        align: 'right',
        render: (value: number | null) => (value != null ? `${formatMetricValue(value)}%` : '-'),
      },
      {
        title: 'Memory Usage',
        dataIndex: 'memoryUsage',
        key: 'memoryUsage',
        width: 140,
        align: 'right',
        render: (value: number | null) => {
          if (value == null) return '-';
          const gb = value / BYTES_TO_GB;
          const mb = value / BYTES_TO_MB;
          return gb >= 1 ? `${formatMetricValue(gb)} GiB` : `${formatMetricValue(mb)} MiB`;
        },
      },
      {
        title: 'Memory Requests',
        dataIndex: 'memoryRequests',
        key: 'memoryRequests',
        width: 160,
        align: 'right',
        render: (value: number | null) => {
          if (value == null) return '-';
          const gb = value / BYTES_TO_GB;
          const mb = value / BYTES_TO_MB;
          return gb >= 1 ? `${formatMetricValue(gb)} GiB` : `${formatMetricValue(mb)} MiB`;
        },
      },
      {
        title: 'Memory Requests %',
        dataIndex: 'memoryRequestsPercent',
        key: 'memoryRequestsPercent',
        width: 180,
        align: 'right',
        render: (value: number | null) => (value != null ? `${formatMetricValue(value)}%` : '-'),
      },
      {
        title: 'Memory Limits',
        dataIndex: 'memoryLimits',
        key: 'memoryLimits',
        width: 140,
        align: 'right',
        render: (value: number | null) => {
          if (value == null) return '-';
          const gb = value / BYTES_TO_GB;
          const mb = value / BYTES_TO_MB;
          return gb >= 1 ? `${formatMetricValue(gb)} GiB` : `${formatMetricValue(mb)} MiB`;
        },
      },
      {
        title: 'Memory Limits %',
        dataIndex: 'memoryLimitsPercent',
        key: 'memoryLimitsPercent',
        width: 160,
        align: 'right',
        render: (value: number | null) => (value != null ? `${formatMetricValue(value)}%` : '-'),
      },
      {
        title: 'Memory Usage (RSS)',
        dataIndex: 'memoryRss',
        key: 'memoryRss',
        width: 180,
        align: 'right',
        render: (value: number | null) => {
          if (value == null) return '-';
          const gb = value / BYTES_TO_GB;
          const mb = value / BYTES_TO_MB;
          return gb >= 1 ? `${formatMetricValue(gb)} GiB` : `${formatMetricValue(mb)} MiB`;
        },
      },
      {
        title: 'Memory Cache',
        dataIndex: 'memoryCache',
        key: 'memoryCache',
        width: 140,
        align: 'right',
        render: (value: number | null) => {
          if (value == null) return '-';
          const gb = value / BYTES_TO_GB;
          const mb = value / BYTES_TO_MB;
          return gb >= 1 ? `${formatMetricValue(gb)} GiB` : `${formatMetricValue(mb)} MiB`;
        },
      },
      {
        title: 'Memory Usage (Swap)',
        dataIndex: 'memorySwap',
        key: 'memorySwap',
        width: 180,
        align: 'right',
        render: (value: number | null) => {
          if (value == null) return '-';
          const gb = value / BYTES_TO_GB;
          const mb = value / BYTES_TO_MB;
          return gb >= 1 ? `${formatMetricValue(gb)} GiB` : `${formatMetricValue(mb)} MiB`;
        },
      },
    ],
    []
  );

  return (
    <Card className={styles.card} hoverable={false}>
      <div className={styles.header}>
        <Title level={5} className={styles.title}>
          Pod Overview
        </Title>
      </div>

      <Table
        columns={columns}
        dataSource={data || []}
        rowKey="pod"
        loading={isLoading}
        pagination={false}
        scroll={{ x: 2200, y: 500 }}
        size="small"
        bordered
        locale={{
          emptyText: isLoading ? '로딩 중...' : '데이터가 없습니다',
        }}
      />
    </Card>
  );
};
